import {
  LoaderFunction,
  LoaderFunctionArgs,
  useFetcher,
} from "react-router-dom";
import { getSearchQuery } from "../query.ts";
import {
  searchLicensesCounts,
  SearchCountResponse,
} from "../../../../common/api/decimal/methods/search.ts";

export interface CountsData {
  results: {
    allResults: SearchCountResponse[] | undefined;
    recentResults?: SearchCountResponse[] | undefined;
    nextCursor: string | undefined;
  } | null;
}

export const searchCounts: LoaderFunction = async ({
  request,
}: LoaderFunctionArgs): Promise<CountsData> => {
  const url = new URL(request.url);
  const query = getSearchQuery(url.searchParams);

  if (query) {
    const [allLicenseResponse, recentSortedResponse] = await Promise.all([
      searchLicensesCounts(query),
      searchLicensesCounts(query, {
        sort: "recent",
        page: { size: 4 },
      }),
    ]);

    const recentResults: SearchCountResponse[] = [];
    if (recentSortedResponse?.data) {
      const currentDate = new Date();
      const threeMonths = 1000 * 60 * 60 * 24 * 30 * 3;

      recentSortedResponse.data.forEach((r) => {
        const lastActivityAt = new Date(r.asset.license.lastActivityAt);
        if (currentDate.getTime() - lastActivityAt.getTime() < threeMonths) {
          recentResults.push(r);
        }
      });
    }

    if (
      [allLicenseResponse?.data, recentResults].some(
        (content) => content && content.length > 0
      )
    ) {
      return Promise.resolve({
        results: {
          allResults: allLicenseResponse?.data,
          recentResults: recentResults.length > 0 ? recentResults : undefined,
          nextCursor: allLicenseResponse.meta?.page?.cursors?.next,
        },
      });
    }
  }
  return Promise.resolve({ results: null });
};

export const searchCountsWithNextCursor: LoaderFunction = async ({
  request,
  params,
}: LoaderFunctionArgs): Promise<CountsData> => {
  const url = new URL(request.url);
  const query = getSearchQuery(url.searchParams);
  const cursor = params.cursor;

  if (query && cursor) {
    const response = await searchLicensesCounts(query, {
      page: { next: cursor },
    });

    if (response.ok) {
      return Promise.resolve({
        results: {
          allResults: response.data,
          nextCursor: response.meta?.page?.cursors?.next,
        },
      });
    }
  }
  return Promise.resolve({ results: null });
};

export function useSearchCountsWithNextCursorFetcher() {
  return useFetcher<CountsData>();
}
